import { useMutation, useQuery } from '@apollo/client'
import { arrayMove } from '@dnd-kit/sortable'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { DUPLICATE_PAGE } from 'data/layoutcreator/mutations/documents'
import {
  REMOVE_PAGE,
  SET_PAGES_ORDER,
  SET_PAGE_WORKFLOW_STATUS
} from 'data/layoutcreator/mutations/projects'
import { GET_PROJECT_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import DndPages from 'shared/components/PageList/components/DndPages'
import { CoverTypes, PageWorkflowStatus } from 'shared/types/global'

import ConfirmDeleteModal from './components/modals/ConfirmDeleteModal'
import PagePreviewModal from './components/modals/PagePreviewModal'
import { useAssumedPageCount } from './hooks/useAssumedPageCount'
import { useLiveblocks } from './hooks/useLiveblocks'
import { usePagePreviews } from './hooks/usePagePreviews'
import { DndPage } from './type'
import { isCoverPage, transformDocumentsToDndPages } from './utils'

type PageListProps = {
  groupId: string
  projectId: string
}

const PageList: React.FC<PageListProps> = ({
  groupId,
  projectId
}) => {
  const projectVars = {
    groupId,
    projectId
  }

  const { formatMessage } = useIntl()

  const navigate = useNavigate()
  const didMountPages = useRef(false)

  const [dndPages, setDndPages] = useState<DndPage[]>([])

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletingPageId, setDeletingPageId] = useState<string | null>(
    null
  )
  const [isPagePreviewModalOpen, setIsPagePreviewModalOpen] =
    useState(false)
  const [pagePreviewPageId, setPagePreviewPageId] = useState<
    string | null
  >(null)

  const { data: projectMetaData } = useQuery(GET_PROJECT_META, {
    variables: projectVars
  })

  const {
    loading: isDocumentsLoading,
    error: documentsError,
    data: documentsData,
    refetch: refetchDocuments
  } = useQuery(GET_PROJECT_DOCUMENTS, {
    variables: projectVars
  })

  const [setPagesOrder] = useMutation(SET_PAGES_ORDER)
  const [removePage] = useMutation(REMOVE_PAGE, {
    refetchQueries: [GET_PROJECT_DOCUMENTS]
  })
  const [duplicatePage] = useMutation(DUPLICATE_PAGE, {
    refetchQueries: [GET_PROJECT_DOCUMENTS]
  })
  const [setPageWorkflowStatus] = useMutation(
    SET_PAGE_WORKFLOW_STATUS
  )

  const { assumedPageCount, savePageCount } = useAssumedPageCount({
    projectId
  })

  const { getPagePreview } = usePagePreviews({
    pages: documentsData?.projectDocuments || [],
    ...projectVars
  })

  const { editingLiveUsersMap, updateMyPresence } = useLiveblocks({
    didMountPages,
    groupId,
    dndPages,
    onUpdate: refetchDocuments
  })

  useEffect(() => {
    if (!documentsData?.projectDocuments) {
      return
    }

    if (!didMountPages.current) {
      // to prevent any setSyncHash (useLiveblocks) if the projects is not loaded yet
      didMountPages.current = true
    }

    setDndPages(
      transformDocumentsToDndPages(documentsData.projectDocuments)
    )
    savePageCount(documentsData.projectDocuments.length)
  }, [documentsData])

  const handleOrderChange = (from: number, to: number) => {
    const newOrderItems = arrayMove(dndPages, from, to)
    setDndPages(newOrderItems)

    setPagesOrder({
      variables: {
        projectId,
        pages: newOrderItems
          .filter(page => !isCoverPage(page.id))
          .map(({ id }, i) => ({ id, index: i }))
      }
    })
  }

  const handlePageDuplicate = async (pageId: string) => {
    if (!pageId) {
      return
    }

    const loadingToast = toast.loading(
      formatMessage({ id: 'Pages.duplicating.page' })
    )

    try {
      await duplicatePage({
        variables: {
          groupId,
          projectId,
          pageId
        }
      })

      toast.success(formatMessage({ id: 'Pages.page.duplicated' }), {
        id: loadingToast
      })
      toast.dismiss(loadingToast)
    } catch (error) {
      toast.error(
        formatMessage(
          { id: 'Pages.page.duplication.error' },
          { id: loadingToast }
        )
      )
      console.error(error)
    }
  }

  const handlePageStatusChange = async (
    pageId: string,
    status: PageWorkflowStatus
  ) => {
    const previousPages = dndPages

    // Optimistically update the UI for instant feedback
    setDndPages(pages =>
      pages.map(page =>
        page.id === pageId && page.contentData
          ? {
              ...page,
              contentData: {
                ...page.contentData,
                workflowStatus: status
              }
            }
          : page
      )
    )

    try {
      await setPageWorkflowStatus({
        variables: { projectId, pageId, status },
        update: cache => {
          cache.modify({
            id: cache.identify({
              __typename: 'ProjectDocument',
              id: pageId
            }),
            fields: {
              workflowStatus: () => status
            }
          })
        }
      })
    } catch (error) {
      // Revert on failure
      setDndPages(previousPages)
      toast.error(formatMessage({ id: 'Pages.page.status.error' }))
      console.error(error)
    }
  }

  const handlePageDelete = async () => {
    if (!deletingPageId) {
      return
    }

    const loadingToast = toast.loading(
      formatMessage({ id: 'Pages.page.deleting' })
    )

    await removePage({
      variables: {
        projectId,
        itemId: deletingPageId
      }
    })

    toast.success(formatMessage({ id: 'Pages.page.deleted' }))
    toast.dismiss(loadingToast)
  }

  return (
    <>
      {documentsError && <p>Error :(</p>}
      <DndPages
        items={dndPages}
        isLoading={isDocumentsLoading}
        assumedPageCount={assumedPageCount}
        getPreviewUrl={getPagePreview}
        editingLiveUsersMap={editingLiveUsersMap}
        onOrderChange={handleOrderChange}
        onPageDragging={updateMyPresence}
        groupId={groupId}
        projectId={projectId}
        coverType={
          projectMetaData?.getProjectMeta
            .coverType as CoverTypes | null
        }
        onPageEditClick={(pageId: string) => {
          navigate(`editor/${pageId}`)
        }}
        onPageCopyClick={handlePageDuplicate}
        onPageViewClick={(pageId: string) => {
          setPagePreviewPageId(pageId)
          setIsPagePreviewModalOpen(true)
        }}
        onPageDeleteClick={(pageId: string) => {
          setDeletingPageId(pageId)
          setIsDeleteModalOpen(true)
        }}
        onPageStatusChange={handlePageStatusChange}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        closeModal={() => {
          setIsDeleteModalOpen(false)
          setDeletingPageId(null)
        }}
        onConfirm={handlePageDelete}
      />

      {pagePreviewPageId && (
        <PagePreviewModal
          projectId={projectVars.projectId}
          groupId={projectVars.groupId}
          previewUrl={
            documentsData?.projectDocuments?.find(
              page => page.id === pagePreviewPageId
            )?.previewUrl!
          }
          pageNumber={
            documentsData?.projectDocuments?.find(
              page => page.id === pagePreviewPageId
            )?.index!
          }
          pageType={
            documentsData?.projectDocuments?.find(
              page => page.id === pagePreviewPageId
            )?.type!
          }
          pageId={pagePreviewPageId}
          isOpen={isPagePreviewModalOpen}
          closeModal={() => setIsPagePreviewModalOpen(false)}
        />
      )}
    </>
  )
}

export default PageList
